import { SessionInitResponse, SessionLongPollResponse } from '@augloop/session-protocol';
import { Utils } from '../utils';
import { asyncFetchWithTimeout, internalFetch, timeoutErrorString } from '../fetch';
import { Headers } from 'cross-fetch';
import { Operation as LoggingOperation, Log, LogCategory } from '@augloop/logging';
const longPollIntervalMs = 15 * 1000;
const longPollFetchTimeoutMs = longPollIntervalMs + 5 * 1000;
const longPollResponseTimeoutString = JSON.stringify(new SessionLongPollResponse({ error: timeoutErrorString }));
export class HttpWorker {
    constructor(networkOverrideOptions) {
        this.networkOverrideOptions = networkOverrideOptions;
        this.isClosed = false;
        // All request messages will be put into this array before we receive the session key.
        this.pendingEgress = [];
    }
    init(url, ingress, onOpen, onClose) {
        const initLogOp = new LoggingOperation({ operationName: 'HttpWorkerInit', success: true }).start();
        const splitUrl = url.split('/?x-origin=');
        if (splitUrl.length === 2) {
            this.url = Utils.convertWebSocketUrlToHttp(splitUrl[0]);
            this.origin = splitUrl[1];
        }
        else {
            this.url = Utils.convertWebSocketUrlToHttp(url);
            this.origin = undefined;
        }
        this.ingress = ingress;
        this.onOpen = onOpen;
        this.onClose = onClose;
        Log.info(0x1e4502cc /* tag_4rqlm */, LogCategory.CoreDefault, initLogOp.stop());
    }
    egress(message) {
        const egressLogOp = new LoggingOperation({ operationName: 'HttpEgress', success: true }).start();
        if (!(this.isConnectedToSession() || message.isHttpSessionInitMessage)) {
            // With the rate controller enabled, this queue shouldn't be populated.
            // Add to the queue if it is not connected or not a session init message
            this.pendingEgress.push(message);
            egressLogOp.success = false;
            egressLogOp.resultDescription = 'NotConnected, pendingQueueSize:  ' + this.pendingEgress.length.toString();
            Log.info(0x1e49f542 /* tag_4s5vc */, LogCategory.CoreDefault, egressLogOp.stop());
            return;
        }
        const requestInfo = this.getRequestInfo(message, egressLogOp);
        if (!requestInfo.url) {
            egressLogOp.success = false;
            egressLogOp.resultDescription = 'Could not generate HTTP request';
            Log.error(0x1e49f541 /* tag_4s5vb */, LogCategory.CoreDefault, egressLogOp.stop());
            return;
        }
        if (message.isHttpSessionLongPollMessage) {
            this.egressLongPoll(requestInfo, egressLogOp);
            return;
        }
        internalFetch(requestInfo.url, requestInfo.request, (error, response) => {
            if (error) {
                this.onEgressError(egressLogOp, 'OnEgressError:' + (error === null || error === void 0 ? void 0 : error.message));
            }
            else if (!response || !response.ok) {
                this.onEgressError(egressLogOp, 'OnEgressResponseError: ' + (error === null || error === void 0 ? void 0 : error.message) + `, Status ${response === null || response === void 0 ? void 0 : response.status}: ${response === null || response === void 0 ? void 0 : response.statusText}`);
            }
            else {
                response.text().then((text) => {
                    egressLogOp.success = true;
                    Log.info(0x1e49f522 /* tag_4s5u8 */, LogCategory.CoreDefault, egressLogOp.stop());
                    this.ingressInternal(text);
                }).catch((error) => {
                    this.onEgressError(egressLogOp, 'OnEgressParseError: ' + (error === null || error === void 0 ? void 0 : error.message));
                });
            }
        });
    }
    close() {
        const closeLogOp = new LoggingOperation({ operationName: 'HttpWorkerClose', success: true }).start();
        if (this.isClosed) {
            // Check if close is already being executed to avoid infinite loop
            closeLogOp.resultDescription = 'AlreadyClosed';
            Log.info(0x1e49f520 /* tag_4s5u6 */, LogCategory.CoreDefault, closeLogOp.stop());
            return;
        }
        Log.info(0x1e49f51f /* tag_4s5u5 */, LogCategory.CoreDefault, closeLogOp.stop());
        this.isClosed = true;
        this.sessionSettings = undefined;
        if (this.onClose) {
            this.onClose(undefined);
            this.onClose = undefined;
        }
    }
    onEgressError(logOp, resultDescription) {
        // Similar to WebSocket, we will close when receiving any kind of error networking response from the service.
        logOp.success = false;
        logOp.resultDescription = resultDescription;
        Log.error(0x1e49f51e /* tag_4s5u4 */, LogCategory.CoreDefault, logOp.stop());
        if (!this.isClosed) {
            this.close();
        }
    }
    ingressInternal(message, fromClient) {
        let input = message;
        if (!fromClient) {
            input = this.formatServerInput(message);
        }
        const onIngressParsed = (response) => {
            // SessionManager parses the response and is sent back down through this callback
            // SessionInitResponse has the session key, origin, sessionUrlBase, and anon token needed to go online
            if (SessionInitResponse.typeGuard(response)) {
                this.onSessionInitResponse(response);
            }
        };
        if (input) {
            this.logIngressCount(input);
            this.ingress(input, onIngressParsed);
        }
    }
    onSessionInitResponse(response) {
        const openLogOp = new LoggingOperation({ operationName: 'HttpWorkerOpen', success: true }).start();
        if (!response.sessionKey || !response.origin || !response.anonymousToken || !response.sessionUrlBase) {
            openLogOp.success = false;
            openLogOp.resultDescription = 'SessionInitResponse missing information';
            Log.error(0x1e44909d /* tag_4rjc3 */, LogCategory.CoreDefault, openLogOp.stop());
            return;
        }
        Log.info(0x1e49f51d /* tag_4s5u3 */, LogCategory.CoreDefault, openLogOp.stop());
        this.setSessionSettings(response.sessionKey, response.origin, response.anonymousToken, response.sessionUrlBase);
        this.onOpen();
        this.pendingEgress.forEach((obj) => {
            this.egress(obj);
        });
        this.pendingEgress = [];
    }
    egressLongPoll(requestInfo, logOp) {
        // TODO: 8230709 Refactor HTTP mode to move egress to HTTP Long Poll manager
        asyncFetchWithTimeout(requestInfo.url, longPollFetchTimeoutMs, requestInfo.request).then((response) => {
            if (!response || !response.ok) {
                this.onEgressError(logOp, 'LongPollFetchStatusFailure: ' + (response === null || response === void 0 ? void 0 : response.statusText));
            }
            else {
                response.text().then((text) => {
                    logOp.success = true;
                    logOp.resourceId = 'LongPoll';
                    Log.info(0x1e49f51b /* tag_4s5u1 */, LogCategory.CoreDefault, logOp.stop());
                    this.ingressInternal(text);
                }).catch((error) => {
                    this.onEgressError(logOp, 'LongPollFetchParseError: ' + (error === null || error === void 0 ? void 0 : error.message));
                });
            }
        }).catch((error) => {
            if (error.message === timeoutErrorString) {
                logOp.success = false;
                logOp.resultDescription = 'LongPollFetchResponseTimeout:' + (error === null || error === void 0 ? void 0 : error.message);
                Log.error(0x1e49f519 /* tag_4s5uz */, LogCategory.CoreDefault, logOp.stop());
                this.ingressInternal(longPollResponseTimeoutString, true /* fromClient */);
            }
            else {
                this.onEgressError(logOp, 'LongPollFetchResponseError: ' + (error === null || error === void 0 ? void 0 : error.message));
            }
        });
    }
    getRequestInfo(message, logOp) {
        const url = this.getUrl(message === null || message === void 0 ? void 0 : message.isHttpSessionInitMessage);
        const headers = this.getHeader(message, logOp);
        logOp.resultSignature = url;
        return {
            url: url,
            request: {
                method: 'POST',
                headers: headers,
                body: message.obj
            }
        };
    }
    getUrl(isHttpSessionInitMessage) {
        const url = (this.sessionSettings && this.sessionSettings.sliceUrl) ? this.sessionSettings.sliceUrl : this.url;
        if (isHttpSessionInitMessage) {
            return url + '/sessioninit';
        }
        else if (this.isConnectedToSession()) {
            return url + '/session/' + this.sessionSettings.sessionKey;
        }
        return '';
    }
    getHeader(message, logOp) {
        var _a, _b;
        const headers = new Headers();
        if (message === null || message === void 0 ? void 0 : message.isHttpSessionInitMessage) {
            headers.set('Content-Type', 'application/json');
            if (this.origin) {
                headers.set('x-origin', this.origin);
            }
        }
        else if (this.isConnectedToSession()) {
            // Add content type header for JSOND2 if enabled
            if (message.obj instanceof Uint8Array || message.obj instanceof ArrayBuffer) {
                headers.set('Content-Type', 'application/jsond2');
                logOp.dimension0 = message.obj.byteLength.toString().length.toString(); //Magnitude of byte length
            }
            else {
                headers.set('Content-Type', 'application/json');
            }
            headers.append('Authorization', `Bearer ${this.sessionSettings.anonymousToken}`);
            headers.set('x-origin', this.sessionSettings.origin);
        }
        if ((_a = this.networkOverrideOptions) === null || _a === void 0 ? void 0 : _a.hostHeader) {
            headers.set('host', (_b = this.networkOverrideOptions) === null || _b === void 0 ? void 0 : _b.hostHeader);
        }
        return headers;
    }
    setSessionSettings(sessionKey, origin, anonymousToken, sessionUrlBase) {
        // Remove /session so we can use it for both sessionInit and session endpoints
        const sliceUrl = sessionUrlBase.replace('/session', '');
        this.sessionSettings = { sessionKey: sessionKey, origin: origin, anonymousToken: anonymousToken, sliceUrl: sliceUrl };
    }
    isConnectedToSession() {
        return this.sessionSettings && this.sessionSettings.sessionKey.length > 0 && this.sessionSettings.anonymousToken.length > 0 && this.sessionSettings.origin.length > 0 && this.sessionSettings.sliceUrl.length > 0 && !this.isClosed;
    }
    formatServerInput(input) {
        // All server responses are put in brackets. Before we send this to ingress(), we need to remove the brackets so that the response is parsed correctly.
        return (input.length > 1) ? input.substring(1, input.length - 1) : '';
    }
    logIngressCount(obj) {
        const byteCount = obj.length;
        if (byteCount > 100000) {
            const ingressByteCountOp = new LoggingOperation({ operationName: 'HttpIngressByteOrderOfMagnitude', success: true }).start();
            ingressByteCountOp.dimension2 = byteCount.toString().length.toString();
            Log.info(0x1e4db716 /* tag_4t12w */, LogCategory.CoreDefault, ingressByteCountOp.stop());
        }
    }
}
//# sourceMappingURL=http-worker.js.map